// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  MapIcon,
  CubeIcon,
  FunnelIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  BoltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { mockSites } from '../data/sitesData';
import { useSiteRestriction } from '../hooks/useSiteRestriction';

interface Activity {
  id: number;
  action: string;
  time: string;
  type: 'success' | 'warning' | 'info' | 'error';
  category: 'shipment' | 'alert' | 'supplier' | 'route' | 'site' | 'system';
  details?: string;
  reference?: string;
  siteId?: string; // Optional site association for filtering
}

// Pallet breakdown data for modal
interface PalletBreakdown {
  division: string;
  pallets: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: string;
}

// Fill rate data for modal
interface FillRateData {
  category: string;
  rate: number;
  target: number;
  shipments: number;
  trend: 'up' | 'down' | 'stable';
}

// Premium transport data for modal
interface PremiumShipment {
  id: string;
  origin: string;
  destination: string;
  reason: string;
  cost: string;
  date: string;
  carrier: string;
}

// Division detail data for modal
interface DivisionDetail {
  name: string;
  dot: number;
  cot: number;
  fillRate: number;
  totalShipments: number;
  totalPallets: number;
  revenue: string;
  costs: string;
  avgLeadTime: string;
  trend: 'up' | 'down' | 'stable';
  trendValue: string;
  topRoutes: { route: string; shipments: number; dot: number }[];
  topCarriers: { carrier: string; shipments: number; rating: number }[];
  recentAlerts: { id: string; message: string; severity: 'high' | 'medium' | 'low'; time: string }[];
  monthlyTrend: { month: string; dot: number; cot: number; fillRate: number }[];
}

type KPIModalType = 'pallets' | 'fillRate' | 'premium' | null;

type TimePeriod = 'today' | 'week' | 'month' | 'quarter';
type DivisionFilter = 'all' | 'chilled' | 'frozen' | 'ambient' | 'fresh';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityFilter, setActivityFilter] = useState<string>('all');
  const [activeKPIModal, setActiveKPIModal] = useState<KPIModalType>(null);
  const [selectedDivision, setSelectedDivision] = useState<string | null>(null);

  // Site restriction hook for Site Managers
  const { isRestricted, restrictedSiteId } = useSiteRestriction();

  // Dashboard filter state
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('today');
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [divisionFilter, setDivisionFilter] = useState<DivisionFilter>('all');

  // Set site filter to restricted site on mount for Site Managers
  useEffect(() => {
    if (isRestricted && restrictedSiteId) {
      setSiteFilter(restrictedSiteId);
    }
  }, [isRestricted, restrictedSiteId]);

  // Time period multipliers for scaling data
  const periodMultipliers: Record<TimePeriod, { value: number; label: string }> = {
    today: { value: 1, label: 'Today' },
    week: { value: 7, label: 'This Week' },
    month: { value: 30, label: 'This Month' },
    quarter: { value: 90, label: 'This Quarter' },
  };

  // Division data weights (for filtering)
  const divisionWeights: Record<DivisionFilter, Record<string, number>> = {
    all: { chilled: 0.36, frozen: 0.27, ambient: 0.23, fresh: 0.14 },
    chilled: { chilled: 1, frozen: 0, ambient: 0, fresh: 0 },
    frozen: { chilled: 0, frozen: 1, ambient: 0, fresh: 0 },
    ambient: { chilled: 0, frozen: 0, ambient: 1, fresh: 0 },
    fresh: { chilled: 0, frozen: 0, ambient: 0, fresh: 1 },
  };

  // Calculate the effective multiplier based on division selection
  const getDivisionMultiplier = () => {
    if (divisionFilter === 'all') return 1;
    return divisionWeights.all[divisionFilter];
  };

  // Calculate the effective multiplier based on site selection
  // Each site contributes roughly equally when "All Sites" is selected
  const getSiteMultiplier = () => {
    if (siteFilter === 'all') return 1;
    // Individual site is roughly 1/number of sites of total volume
    return 1 / mockSites.length;
  };

  // Format large numbers based on period
  const formatValue = (baseValue: number, includeDecimals = false) => {
    const multiplied = baseValue * periodMultipliers[timePeriod].value * getDivisionMultiplier() * getSiteMultiplier();
    if (includeDecimals) {
      return multiplied.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    return Math.round(multiplied).toLocaleString();
  };

  // Calculate KPI values based on filters
  const getKPIs = () => {
    const baseShipments = 2847;
    const basePallets = 68245;
    const multiplier = periodMultipliers[timePeriod].value * getDivisionMultiplier() * getSiteMultiplier();

    // Base rates vary slightly by division
    const divisionRates: Record<DivisionFilter, { fillRate: number; dot: number; cot: number; premium: number }> = {
      all: { fillRate: 87.2, dot: 94.2, cot: 91.8, premium: 4.2 },
      chilled: { fillRate: 88.5, dot: 95.2, cot: 93.1, premium: 3.8 },
      frozen: { fillRate: 85.2, dot: 92.8, cot: 90.4, premium: 5.1 },
      ambient: { fillRate: 89.1, dot: 94.5, cot: 91.7, premium: 3.2 },
      fresh: { fillRate: 82.3, dot: 93.1, cot: 89.8, premium: 5.8 },
    };

    const rates = divisionRates[divisionFilter];

    return [
      { name: 'Total Shipments', value: formatValue(baseShipments), change: '+12%', positive: true, icon: '📦', action: 'navigate', target: '/shipments' },
      { name: 'Total Pallets', value: formatValue(basePallets), change: '+8%', positive: true, icon: '🏗️', action: 'modal', target: 'pallets' },
      { name: 'Avg Fill Rate', value: `${rates.fillRate}%`, change: '+3.2%', positive: true, icon: '📊', action: 'modal', target: 'fillRate' },
      { name: 'On-Time Delivery (DOT)', value: `${rates.dot}%`, change: '+1.8%', positive: true, icon: '✅', action: 'navigate', target: '/suppliers' },
      { name: 'On-Time Collection (COT)', value: `${rates.cot}%`, change: rates.cot >= 92 ? '+0.5%' : '-0.5%', positive: rates.cot >= 92, icon: '⏰', action: 'navigate', target: '/suppliers' },
      { name: 'Premium Transport', value: `${rates.premium}%`, change: '-2.1%', positive: true, icon: '🚛', action: 'modal', target: 'premium' },
    ];
  };

  const kpis = getKPIs();

  // Calculate cost values based on filters
  const getCostData = () => {
    const baseMTDSpend = 4200000; // £4.2M base
    const baseSavings = 125000; // £125K
    const baseUnplanned = 85000; // £85K
    const baseBudget = 5400000; // £5.4M

    // Division-specific cost multipliers
    const divisionCostMultipliers: Record<DivisionFilter, number> = {
      all: 1,
      chilled: 0.38,
      frozen: 0.32,
      ambient: 0.18,
      fresh: 0.12,
    };

    // Time period affects cumulative costs
    const periodCostMultipliers: Record<TimePeriod, number> = {
      today: 0.033, // ~1/30th of month
      week: 0.23, // ~1/4 of month
      month: 1,
      quarter: 3,
    };

    const multiplier = divisionCostMultipliers[divisionFilter] * periodCostMultipliers[timePeriod] * getSiteMultiplier();

    const mtdSpend = baseMTDSpend * multiplier;
    const savings = baseSavings * multiplier;
    const unplanned = baseUnplanned * multiplier;
    const budget = baseBudget * multiplier;
    const utilization = Math.round((mtdSpend / budget) * 100);

    // Format currency
    const formatCurrency = (value: number) => {
      if (value >= 1000000) {
        return `£${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `£${Math.round(value / 1000)}K`;
      }
      return `£${Math.round(value)}`;
    };

    return {
      mtdSpend: formatCurrency(mtdSpend),
      savings: formatCurrency(savings),
      unplanned: formatCurrency(unplanned),
      budget: formatCurrency(budget),
      utilization,
      costPerPalletMile: divisionFilter === 'all' ? '£1.24' :
        divisionFilter === 'chilled' ? '£1.42' :
        divisionFilter === 'frozen' ? '£1.68' :
        divisionFilter === 'ambient' ? '£0.98' : '£1.15',
    };
  };

  const costData = getCostData();

  // Static KPI data (kept for reference)
  const staticKpis = [
    { name: 'Total Shipments', value: '2,847', change: '+12%', positive: true, icon: '📦', action: 'navigate', target: '/shipments' },
    { name: 'Total Pallets', value: '68,245', change: '+8%', positive: true, icon: '🏗️', action: 'modal', target: 'pallets' },
    { name: 'Avg Fill Rate', value: '87.2%', change: '+3.2%', positive: true, icon: '📊', action: 'modal', target: 'fillRate' },
    { name: 'On-Time Delivery (DOT)', value: '94.2%', change: '+1.8%', positive: true, icon: '✅', action: 'navigate', target: '/suppliers' },
    { name: 'On-Time Collection (COT)', value: '91.8%', change: '-0.5%', positive: false, icon: '⏰', action: 'navigate', target: '/suppliers' },
    { name: 'Premium Transport', value: '4.2%', change: '-2.1%', positive: true, icon: '🚛', action: 'modal', target: 'premium' },
  ];

  // Pallet breakdown dummy data
  const palletBreakdown: PalletBreakdown[] = [
    { division: 'Chilled', pallets: 24650, percentage: 36.1, trend: 'up', trendValue: '+5.2%' },
    { division: 'Frozen', pallets: 18420, percentage: 27.0, trend: 'up', trendValue: '+3.8%' },
    { division: 'Ambient', pallets: 15890, percentage: 23.3, trend: 'stable', trendValue: '+0.4%' },
    { division: 'Fresh', pallets: 9285, percentage: 13.6, trend: 'down', trendValue: '-1.2%' },
  ];

  const palletsByDestination = [
    { site: 'Manchester DC', pallets: 12450, percentage: 18.2 },
    { site: 'Birmingham Warehouse', pallets: 10280, percentage: 15.1 },
    { site: 'Leeds Distribution', pallets: 9840, percentage: 14.4 },
    { site: 'Glasgow Hub', pallets: 8920, percentage: 13.1 },
    { site: 'Bristol Site', pallets: 7650, percentage: 11.2 },
    { site: 'Other Sites', pallets: 19105, percentage: 28.0 },
  ];

  // Fill rate dummy data
  const fillRateData: FillRateData[] = [
    { category: 'Chilled', rate: 88.5, target: 90, shipments: 842, trend: 'up' },
    { category: 'Frozen', rate: 85.2, target: 88, shipments: 623, trend: 'down' },
    { category: 'Ambient', rate: 89.1, target: 88, shipments: 956, trend: 'up' },
    { category: 'Fresh', rate: 82.3, target: 85, shipments: 426, trend: 'stable' },
  ];

  const fillRateByCarrier = [
    { carrier: 'Swift Transport', rate: 91.2, shipments: 524 },
    { carrier: 'Express Freight', rate: 88.7, shipments: 412 },
    { carrier: 'Northern Haulage', rate: 87.3, shipments: 389 },
    { carrier: 'FastFreight Ltd', rate: 85.9, shipments: 298 },
    { carrier: 'GreenLine Transport', rate: 84.2, shipments: 224 },
  ];

  // Premium transport dummy data
  const premiumShipments: PremiumShipment[] = [
    { id: 'SHP-2845', origin: 'Manchester DC', destination: 'Edinburgh', reason: 'Customer escalation - urgent delivery', cost: '£2,450', date: 'Today, 09:30', carrier: 'Express Freight' },
    { id: 'SHP-2841', origin: 'Leeds Warehouse', destination: 'London', reason: 'Vehicle breakdown - replacement required', cost: '£1,890', date: 'Today, 06:15', carrier: 'Swift Transport' },
    { id: 'SHP-2838', origin: 'Bristol Site', destination: 'Cardiff', reason: 'Temperature excursion - expedited transfer', cost: '£980', date: 'Yesterday, 18:45', carrier: 'Chilled Express' },
    { id: 'SHP-2832', origin: 'Birmingham', destination: 'Newcastle', reason: 'Stock-out prevention - emergency restock', cost: '£1,650', date: 'Yesterday, 14:20', carrier: 'Northern Haulage' },
    { id: 'SHP-2828', origin: 'Glasgow Hub', destination: 'Aberdeen', reason: 'Missed collection window', cost: '£1,120', date: 'Yesterday, 08:00', carrier: 'Highland Logistics' },
    { id: 'SHP-2819', origin: 'Southampton', destination: 'Bristol', reason: 'Customer special request', cost: '£875', date: '2 days ago', carrier: 'Express Freight' },
  ];

  const premiumSummary = {
    totalShipments: 119,
    totalCost: '£145,680',
    avgCostPerShipment: '£1,224',
    mainReasons: [
      { reason: 'Customer escalations', count: 42, percentage: 35.3 },
      { reason: 'Vehicle breakdowns', count: 28, percentage: 23.5 },
      { reason: 'Missed windows', count: 24, percentage: 20.2 },
      { reason: 'Stock emergencies', count: 15, percentage: 12.6 },
      { reason: 'Other', count: 10, percentage: 8.4 },
    ],
  };

  // Mock recent activity (shown on dashboard) - assign to sites from mockSites
  const siteIds = mockSites.map(s => s.id);
  const recentActivity: Activity[] = [
    { id: 1, action: `Shipment SHP-2847 delivered to ${mockSites[0]?.name || 'Site'}`, time: '5 min ago', type: 'success', category: 'shipment', reference: 'SHP-2847', siteId: siteIds[0] },
    { id: 2, action: 'Alert: SHP-2845 delayed due to traffic', time: '12 min ago', type: 'warning', category: 'alert', reference: 'SHP-2845', siteId: siteIds[1] },
    { id: 3, action: 'New supplier onboarded: FastFreight Ltd', time: '1 hour ago', type: 'info', category: 'supplier', reference: 'SUP-0892' },
    { id: 4, action: 'Route optimization saved 45 miles on Route R-123', time: '2 hours ago', type: 'success', category: 'route', reference: 'R-123', siteId: siteIds[2] },
    { id: 5, action: `Capacity warning: ${mockSites[3]?.name || 'Site'} at 92%`, time: '3 hours ago', type: 'warning', category: 'site', reference: 'SITE-001', siteId: siteIds[3] },
  ];

  // Extended activity log for modal
  const allActivity: Activity[] = [
    ...recentActivity,
    { id: 6, action: `Shipment SHP-2846 picked up from ${mockSites[4]?.name || 'Warehouse'}`, time: '3 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2846', details: 'Carrier: Swift Transport, 24 pallets', siteId: siteIds[4] },
    { id: 7, action: 'System maintenance completed successfully', time: '4 hours ago', type: 'info', category: 'system', details: 'Database optimization and backup completed' },
    { id: 8, action: `Shipment SHP-2844 delivered to ${mockSites[5]?.name || 'Site'}`, time: '4 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2844', details: '18 pallets, On-time delivery', siteId: siteIds[5] },
    { id: 9, action: 'Alert resolved: SHP-2840 back on schedule', time: '5 hours ago', type: 'success', category: 'alert', reference: 'SHP-2840', details: 'Delay resolved after route adjustment', siteId: siteIds[0] },
    { id: 10, action: 'New route created: Express Route (R-156)', time: '5 hours ago', type: 'info', category: 'route', reference: 'R-156', details: '3 stops, estimated 285 miles', siteId: siteIds[1] },
    { id: 11, action: 'Supplier performance review: Acme Logistics rated 4.5/5', time: '6 hours ago', type: 'info', category: 'supplier', reference: 'SUP-0234', details: 'DOT: 96.2%, COT: 94.1%' },
    { id: 12, action: `Shipment SHP-2843 in transit to ${mockSites[2]?.name || 'Site'}`, time: '6 hours ago', type: 'info', category: 'shipment', reference: 'SHP-2843', details: 'ETA: 2 hours', siteId: siteIds[2] },
    { id: 13, action: `Critical: ${mockSites[6]?.name || 'Site'} approaching capacity (95%)`, time: '7 hours ago', type: 'error', category: 'site', reference: 'SITE-002', details: 'Recommend redistributing inventory', siteId: siteIds[6] },
    { id: 14, action: `Shipment SHP-2842 delivered to ${mockSites[7]?.name || 'Site'}`, time: '7 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2842', details: '32 pallets, Chilled division', siteId: siteIds[7] },
    { id: 15, action: 'Route R-089 optimization complete', time: '8 hours ago', type: 'success', category: 'route', reference: 'R-089', details: 'Reduced distance by 12%, fuel savings estimated £240', siteId: siteIds[3] },
    { id: 16, action: 'Supplier contract renewed: Northern Haulage', time: '8 hours ago', type: 'info', category: 'supplier', reference: 'SUP-0156', details: '12-month contract, preferred rates applied' },
    { id: 17, action: 'Shipment SHP-2841 delayed - vehicle breakdown', time: '9 hours ago', type: 'warning', category: 'alert', reference: 'SHP-2841', details: 'Replacement vehicle dispatched, new ETA +3 hours', siteId: siteIds[4] },
    { id: 18, action: `${mockSites[0]?.name || 'Site'} capacity normalized (78%)`, time: '9 hours ago', type: 'success', category: 'site', reference: 'SITE-003', details: 'Outbound shipments cleared backlog', siteId: siteIds[0] },
    { id: 19, action: `Shipment SHP-2839 delivered to ${mockSites[8]?.name || 'Site'}`, time: '10 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2839', details: '15 pallets, Ambient division', siteId: siteIds[8] },
    { id: 20, action: 'Weekly performance report generated', time: '10 hours ago', type: 'info', category: 'system', details: 'Report available in Analytics section' },
    { id: 21, action: 'Alert: Temperature deviation on SHP-2838 (Frozen)', time: '11 hours ago', type: 'error', category: 'alert', reference: 'SHP-2838', details: 'Temperature rose to -15°C, monitoring closely', siteId: siteIds[5] },
    { id: 22, action: `Shipment SHP-2837 picked up from ${mockSites[3]?.name || 'Warehouse'}`, time: '11 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2837', details: 'Carrier: Express Freight, 28 pallets', siteId: siteIds[3] },
    { id: 23, action: 'New supplier added: GreenLine Transport', time: '12 hours ago', type: 'info', category: 'supplier', reference: 'SUP-0893', details: 'Specializes in sustainable logistics' },
    { id: 24, action: 'Route R-145 delayed due to road closure', time: '12 hours ago', type: 'warning', category: 'route', reference: 'R-145', details: 'Alternative route being calculated', siteId: siteIds[1] },
    { id: 25, action: `Shipment SHP-2836 delivered to ${mockSites[9]?.name || 'Port'}`, time: '13 hours ago', type: 'success', category: 'shipment', reference: 'SHP-2836', details: '45 pallets, Export shipment', siteId: siteIds[9] },
  ];

  // Filter activity by selected site
  const getFilteredActivity = (activities: Activity[]) => {
    if (siteFilter === 'all') return activities;
    return activities.filter(a => !a.siteId || a.siteId === siteFilter);
  };

  const filteredRecentActivity = getFilteredActivity(recentActivity);
  const filteredAllActivity = getFilteredActivity(allActivity);

  // Mock division performance - base data varies slightly by site
  const getBaseDivisionPerformance = () => {
    // Generate slightly different metrics based on site selection
    const siteVariance = siteFilter === 'all' ? 0 :
      // Use site ID to create deterministic variance (-2 to +2)
      ((siteFilter.charCodeAt(siteFilter.length - 1) % 5) - 2);

    return [
      { name: 'Chilled', dot: Math.min(99, Math.max(85, 95.2 + siteVariance * 0.3)), cot: Math.min(99, Math.max(85, 93.1 + siteVariance * 0.4)), fillRate: Math.min(99, Math.max(75, 88.5 + siteVariance * 0.5)) },
      { name: 'Frozen', dot: Math.min(99, Math.max(85, 92.8 + siteVariance * 0.2)), cot: Math.min(99, Math.max(85, 90.4 + siteVariance * 0.3)), fillRate: Math.min(99, Math.max(75, 85.2 + siteVariance * 0.4)) },
      { name: 'Ambient', dot: Math.min(99, Math.max(85, 94.5 + siteVariance * 0.4)), cot: Math.min(99, Math.max(85, 91.7 + siteVariance * 0.2)), fillRate: Math.min(99, Math.max(75, 89.1 + siteVariance * 0.3)) },
      { name: 'Fresh', dot: Math.min(99, Math.max(85, 93.1 + siteVariance * 0.5)), cot: Math.min(99, Math.max(85, 89.8 + siteVariance * 0.5)), fillRate: Math.min(99, Math.max(75, 82.3 + siteVariance * 0.6)) },
    ].map(d => ({
      ...d,
      dot: Number(d.dot.toFixed(1)),
      cot: Number(d.cot.toFixed(1)),
      fillRate: Number(d.fillRate.toFixed(1)),
    }));
  };

  const allDivisionPerformance = getBaseDivisionPerformance();

  // Filter division performance based on selected division
  const divisionPerformance = divisionFilter === 'all'
    ? allDivisionPerformance
    : allDivisionPerformance.filter(d => d.name.toLowerCase() === divisionFilter);

  // Detailed division data for modal
  const divisionDetails: Record<string, DivisionDetail> = {
    'Chilled': {
      name: 'Chilled',
      dot: 95.2,
      cot: 93.1,
      fillRate: 88.5,
      totalShipments: 842,
      totalPallets: 24650,
      revenue: '£1.8M',
      costs: '£1.2M',
      avgLeadTime: '18.5 hrs',
      trend: 'up',
      trendValue: '+2.4%',
      topRoutes: [
        { route: 'Manchester → Birmingham', shipments: 124, dot: 96.8 },
        { route: 'Leeds → Newcastle', shipments: 98, dot: 95.2 },
        { route: 'Bristol → Cardiff', shipments: 87, dot: 94.5 },
        { route: 'Glasgow → Edinburgh', shipments: 76, dot: 97.1 },
        { route: 'London → Southampton', shipments: 65, dot: 93.8 },
      ],
      topCarriers: [
        { carrier: 'Swift Transport', shipments: 245, rating: 4.8 },
        { carrier: 'Chilled Express', shipments: 189, rating: 4.6 },
        { carrier: 'Northern Haulage', shipments: 156, rating: 4.5 },
        { carrier: 'FastFreight Ltd', shipments: 134, rating: 4.3 },
      ],
      recentAlerts: [
        { id: 'ALT-1245', message: 'Temperature deviation on route M62', severity: 'high', time: '2 hours ago' },
        { id: 'ALT-1242', message: 'Delivery delay to Manchester DC', severity: 'medium', time: '5 hours ago' },
        { id: 'ALT-1238', message: 'Capacity warning at Leeds cold storage', severity: 'low', time: '8 hours ago' },
      ],
      monthlyTrend: [
        { month: 'Sep', dot: 93.2, cot: 91.0, fillRate: 86.2 },
        { month: 'Oct', dot: 94.1, cot: 92.0, fillRate: 87.5 },
        { month: 'Nov', dot: 94.8, cot: 92.5, fillRate: 88.0 },
        { month: 'Dec', dot: 95.2, cot: 93.1, fillRate: 88.5 },
      ],
    },
    'Frozen': {
      name: 'Frozen',
      dot: 92.8,
      cot: 90.4,
      fillRate: 85.2,
      totalShipments: 623,
      totalPallets: 18420,
      revenue: '£1.4M',
      costs: '£980K',
      avgLeadTime: '22.3 hrs',
      trend: 'stable',
      trendValue: '+0.3%',
      topRoutes: [
        { route: 'Grimsby → Manchester', shipments: 112, dot: 93.5 },
        { route: 'Hull → Leeds', shipments: 89, dot: 92.1 },
        { route: 'Aberdeen → Glasgow', shipments: 78, dot: 91.8 },
        { route: 'Liverpool → Birmingham', shipments: 67, dot: 94.2 },
        { route: 'Southampton → London', shipments: 54, dot: 90.5 },
      ],
      topCarriers: [
        { carrier: 'Arctic Logistics', shipments: 198, rating: 4.5 },
        { carrier: 'Frozen Express', shipments: 167, rating: 4.4 },
        { carrier: 'ColdChain UK', shipments: 134, rating: 4.2 },
        { carrier: 'Northern Frozen', shipments: 98, rating: 4.1 },
      ],
      recentAlerts: [
        { id: 'ALT-1248', message: 'Compressor warning on vehicle FZ-234', severity: 'high', time: '1 hour ago' },
        { id: 'ALT-1244', message: 'Extended transit time on A1 route', severity: 'medium', time: '4 hours ago' },
        { id: 'ALT-1240', message: 'Stock level low at Grimsby depot', severity: 'medium', time: '6 hours ago' },
      ],
      monthlyTrend: [
        { month: 'Sep', dot: 91.5, cot: 89.2, fillRate: 84.0 },
        { month: 'Oct', dot: 92.0, cot: 89.8, fillRate: 84.5 },
        { month: 'Nov', dot: 92.5, cot: 90.1, fillRate: 85.0 },
        { month: 'Dec', dot: 92.8, cot: 90.4, fillRate: 85.2 },
      ],
    },
    'Ambient': {
      name: 'Ambient',
      dot: 94.5,
      cot: 91.7,
      fillRate: 89.1,
      totalShipments: 956,
      totalPallets: 15890,
      revenue: '£1.2M',
      costs: '£720K',
      avgLeadTime: '14.2 hrs',
      trend: 'up',
      trendValue: '+1.8%',
      topRoutes: [
        { route: 'Midlands Hub → London', shipments: 156, dot: 95.8 },
        { route: 'Manchester → Liverpool', shipments: 134, dot: 94.2 },
        { route: 'Bristol → Exeter', shipments: 98, dot: 93.5 },
        { route: 'Newcastle → Edinburgh', shipments: 87, dot: 95.1 },
        { route: 'Birmingham → Nottingham', shipments: 76, dot: 94.8 },
      ],
      topCarriers: [
        { carrier: 'Express Freight', shipments: 287, rating: 4.7 },
        { carrier: 'Swift Transport', shipments: 234, rating: 4.6 },
        { carrier: 'Midlands Haulage', shipments: 189, rating: 4.4 },
        { carrier: 'UK Direct', shipments: 145, rating: 4.3 },
      ],
      recentAlerts: [
        { id: 'ALT-1246', message: 'High volume warning for next week', severity: 'low', time: '3 hours ago' },
        { id: 'ALT-1241', message: 'Route optimization available', severity: 'low', time: '7 hours ago' },
      ],
      monthlyTrend: [
        { month: 'Sep', dot: 92.8, cot: 89.5, fillRate: 87.2 },
        { month: 'Oct', dot: 93.5, cot: 90.2, fillRate: 88.0 },
        { month: 'Nov', dot: 94.0, cot: 91.0, fillRate: 88.5 },
        { month: 'Dec', dot: 94.5, cot: 91.7, fillRate: 89.1 },
      ],
    },
    'Fresh': {
      name: 'Fresh',
      dot: 93.1,
      cot: 89.8,
      fillRate: 82.3,
      totalShipments: 426,
      totalPallets: 9285,
      revenue: '£890K',
      costs: '£580K',
      avgLeadTime: '8.5 hrs',
      trend: 'down',
      trendValue: '-1.2%',
      topRoutes: [
        { route: 'Kent → London', shipments: 89, dot: 94.2 },
        { route: 'Cornwall → Bristol', shipments: 67, dot: 92.5 },
        { route: 'Norfolk → Cambridge', shipments: 56, dot: 93.8 },
        { route: 'Yorkshire → Manchester', shipments: 45, dot: 91.2 },
        { route: 'Scotland → Newcastle', shipments: 38, dot: 94.5 },
      ],
      topCarriers: [
        { carrier: 'Fresh Direct', shipments: 156, rating: 4.4 },
        { carrier: 'Farm to Fork Logistics', shipments: 123, rating: 4.3 },
        { carrier: 'Green Mile Transport', shipments: 89, rating: 4.1 },
        { carrier: 'Local Fresh Ltd', shipments: 58, rating: 4.0 },
      ],
      recentAlerts: [
        { id: 'ALT-1249', message: 'Quality issue reported - batch FR-2847', severity: 'high', time: '30 mins ago' },
        { id: 'ALT-1247', message: 'Short shelf life items require priority', severity: 'high', time: '2 hours ago' },
        { id: 'ALT-1243', message: 'Supplier delay affecting morning deliveries', severity: 'medium', time: '5 hours ago' },
        { id: 'ALT-1239', message: 'Fill rate below target - review needed', severity: 'medium', time: '8 hours ago' },
      ],
      monthlyTrend: [
        { month: 'Sep', dot: 94.2, cot: 91.5, fillRate: 84.5 },
        { month: 'Oct', dot: 93.8, cot: 90.8, fillRate: 83.8 },
        { month: 'Nov', dot: 93.5, cot: 90.2, fillRate: 83.0 },
        { month: 'Dec', dot: 93.1, cot: 89.8, fillRate: 82.3 },
      ],
    },
  };

  // Quick actions
  const quickActions = [
    { name: 'Control Tower', icon: '🗼', path: '/control-tower', color: 'bg-teal-500' },
    { name: 'New Shipment', icon: '➕', path: '/shipments', color: 'bg-blue-500' },
    { name: 'View Alerts', icon: '🔔', path: '/alerts', color: 'bg-red-500' },
    { name: 'Analytics', icon: '📈', path: '/analytics', color: 'bg-purple-500' },
  ];

  const handleKPIClick = (kpi: typeof kpis[0]) => {
    if (kpi.action === 'navigate') {
      navigate(kpi.target);
    } else if (kpi.action === 'modal') {
      setActiveKPIModal(kpi.target as KPIModalType);
    }
  };

  const getActivityIcon = (category: Activity['category']) => {
    switch (category) {
      case 'shipment':
        return <TruckIcon className="w-5 h-5" />;
      case 'alert':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      case 'supplier':
        return <UserGroupIcon className="w-5 h-5" />;
      case 'route':
        return <MapIcon className="w-5 h-5" />;
      case 'site':
        return <BuildingOffice2Icon className="w-5 h-5" />;
      case 'system':
        return <CubeIcon className="w-5 h-5" />;
      default:
        return <InformationCircleIcon className="w-5 h-5" />;
    }
  };

  const getActivityTypeStyles = (type: Activity['type']) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-100 dark:bg-green-900/30',
          text: 'text-green-600 dark:text-green-400',
          dot: 'bg-green-500',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-100 dark:bg-yellow-900/30',
          text: 'text-yellow-600 dark:text-yellow-400',
          dot: 'bg-yellow-500',
        };
      case 'error':
        return {
          bg: 'bg-red-100 dark:bg-red-900/30',
          text: 'text-red-600 dark:text-red-400',
          dot: 'bg-red-500',
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-100 dark:bg-blue-900/30',
          text: 'text-blue-600 dark:text-blue-400',
          dot: 'bg-blue-500',
        };
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    if (trend === 'up') return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
    return <span className="w-4 h-4 text-gray-400">―</span>;
  };

  const filteredActivity = activityFilter === 'all'
    ? filteredAllActivity
    : filteredAllActivity.filter(a => a.category === activityFilter);

  const activityCategories = [
    { value: 'all', label: 'All Activity' },
    { value: 'shipment', label: 'Shipments' },
    { value: 'alert', label: 'Alerts' },
    { value: 'supplier', label: 'Suppliers' },
    { value: 'route', label: 'Routes' },
    { value: 'site', label: 'Sites' },
    { value: 'system', label: 'System' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Welcome to Auxilio Logistics
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {periodMultipliers[timePeriod].label}'s overview
            {siteFilter !== 'all' && ` at ${mockSites.find(s => s.id === siteFilter)?.name || siteFilter}`}
            {divisionFilter !== 'all' && ` for ${divisionFilter.charAt(0).toUpperCase() + divisionFilter.slice(1)} division`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>
          <div className="relative">
            <select
              value={isRestricted && restrictedSiteId ? restrictedSiteId : siteFilter}
              onChange={(e) => !isRestricted && setSiteFilter(e.target.value)}
              disabled={isRestricted}
              className={`px-4 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                isRestricted ? 'pr-10 bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''
              }`}
            >
              {!isRestricted && <option value="all">All Sites</option>}
              {isRestricted && restrictedSiteId ? (
                <option value={restrictedSiteId}>
                  {mockSites.find(s => s.id === restrictedSiteId)?.name || restrictedSiteId}
                </option>
              ) : (
                mockSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))
              )}
            </select>
            {isRestricted && (
              <LockClosedIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            )}
          </div>
          <select
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value as DivisionFilter)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="all">All Divisions</option>
            <option value="chilled">Chilled</option>
            <option value="frozen">Frozen</option>
            <option value="ambient">Ambient</option>
            <option value="fresh">Fresh</option>
          </select>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, index) => (
          <button
            key={index}
            onClick={() => navigate(action.path)}
            className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 dark:border-gray-700"
          >
            <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center text-2xl`}>
              {action.icon}
            </div>
            <span className="font-medium text-gray-900 dark:text-white">{action.name}</span>
          </button>
        ))}
      </div>

      {/* KPI Cards - Now Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi, index) => (
          <button
            key={index}
            onClick={() => handleKPIClick(kpi)}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700 transition-all text-left group"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {kpi.name}
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {kpi.value}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{kpi.icon}</span>
                <ChevronRightIcon className="w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className={`text-sm font-medium ${kpi.positive ? 'text-green-600' : 'text-red-600'}`}>
                  {kpi.change}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">vs last period</span>
              </div>
              <span className="text-xs text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity">
                Click for details
              </span>
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Division Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Division Performance
            </h3>
          </div>
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left">
                    <th className="pb-3 text-sm font-medium text-gray-500 dark:text-gray-400">Division</th>
                    <th className="pb-3 text-sm font-medium text-gray-500 dark:text-gray-400">DOT %</th>
                    <th className="pb-3 text-sm font-medium text-gray-500 dark:text-gray-400">COT %</th>
                    <th className="pb-3 text-sm font-medium text-gray-500 dark:text-gray-400">Fill Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {divisionPerformance.map((div, index) => (
                    <tr
                      key={index}
                      onClick={() => setSelectedDivision(div.name)}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <td className="py-3 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          {div.name}
                          <ChevronRightIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </td>
                      <td className="py-3">
                        <span className={`font-medium ${div.dot >= 94 ? 'text-green-600' : div.dot >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {div.dot}%
                        </span>
                      </td>
                      <td className="py-3">
                        <span className={`font-medium ${div.cot >= 92 ? 'text-green-600' : div.cot >= 88 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {div.cot}%
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${div.fillRate >= 85 ? 'bg-green-500' : div.fillRate >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`}
                              style={{ width: `${div.fillRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{div.fillRate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Activity
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {filteredRecentActivity.slice(0, 5).map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                  activity.type === 'success' ? 'bg-green-500' :
                  activity.type === 'warning' ? 'bg-yellow-500' :
                  'bg-blue-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white">{activity.action}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowActivityModal(true)}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              View all activity →
            </button>
          </div>
        </div>
      </div>

      {/* Cost Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Transport Cost Overview
          </h3>
          <button
            onClick={() => navigate('/analytics')}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            View detailed analytics →
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{costData.mtdSpend}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {timePeriod === 'today' ? 'Daily' : timePeriod === 'week' ? 'Weekly' : timePeriod === 'month' ? 'MTD' : 'QTD'} Spend
              </p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{costData.savings}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Savings vs Budget</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">{costData.unplanned}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Unplanned Spend</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-teal-600">{costData.costPerPalletMile}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cost per Pallet Mile</p>
            </div>
          </div>

          {/* Simple progress bar representation */}
          <div className="mt-8">
            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
              <span>Budget Utilization</span>
              <span>{costData.utilization}% of {costData.budget}</span>
            </div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full" style={{ width: `${costData.utilization}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Activity Log
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  All recent activity across your logistics network
                </p>
              </div>
              <button
                onClick={() => setShowActivityModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Filter Bar */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-3">
                <FunnelIcon className="w-5 h-5 text-gray-400" />
                <div className="flex flex-wrap gap-2">
                  {activityCategories.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setActivityFilter(cat.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        activityFilter === cat.value
                          ? 'bg-teal-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Activity List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {filteredActivity.map((activity) => {
                  const styles = getActivityTypeStyles(activity.type);
                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {/* Icon */}
                      <div className={`p-2 rounded-lg ${styles.bg} ${styles.text}`}>
                        {getActivityIcon(activity.category)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {activity.action}
                            </p>
                            {activity.details && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {activity.details}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {activity.time}
                            </p>
                            {activity.reference && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                {activity.reference}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status indicator */}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles.bg} ${styles.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
                            {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {activity.category.charAt(0).toUpperCase() + activity.category.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredActivity.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">
                    No activity found for this filter.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredActivity.length} of {filteredAllActivity.length} activities
                {siteFilter !== 'all' && ` (filtered by ${mockSites.find(s => s.id === siteFilter)?.name || 'site'})`}
              </p>
              <button
                onClick={() => setShowActivityModal(false)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pallets Breakdown Modal */}
      {activeKPIModal === 'pallets' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
                  <CubeIcon className="w-8 h-8 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Pallet Breakdown
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    68,245 total pallets this period
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">68,245</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Pallets</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">+8%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">vs Last Period</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">24</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Avg per Shipment</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-teal-600">2,847</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Shipments</p>
                </div>
              </div>

              {/* By Division */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Division</h3>
                <div className="space-y-3">
                  {palletBreakdown.map((item) => (
                    <div key={item.division} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-white">{item.division}</span>
                          <div className="flex items-center gap-1">
                            {getTrendIcon(item.trend)}
                            <span className={`text-sm ${item.trend === 'up' ? 'text-green-600' : item.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
                              {item.trendValue}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900 dark:text-white">{item.pallets.toLocaleString()}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({item.percentage}%)</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Destination */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Destination</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Site</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Pallets</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {palletsByDestination.map((site) => (
                        <tr key={site.site}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{site.site}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{site.pallets.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-500 dark:text-gray-400">{site.percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <button
                onClick={() => navigate('/shipments')}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                View all shipments →
              </button>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fill Rate Modal */}
      {activeKPIModal === 'fillRate' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <CheckCircleIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Fill Rate Analysis
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Average fill rate: 87.2% (+3.2% vs last period)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* What is Fill Rate */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Fill Rate</strong> measures the percentage of customer orders shipped complete. A higher fill rate means fewer backorders and better customer satisfaction.
                </p>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">87.2%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Overall Rate</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-teal-600">88%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Target</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">+3.2%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">vs Last Period</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">2,847</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Shipments</p>
                </div>
              </div>

              {/* By Division */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Division</h3>
                <div className="space-y-4">
                  {fillRateData.map((item) => (
                    <div key={item.category} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-white">{item.category}</span>
                          {getTrendIcon(item.trend)}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400">{item.shipments} shipments</span>
                          <span className={`font-semibold ${item.rate >= item.target ? 'text-green-600' : 'text-yellow-600'}`}>
                            {item.rate}%
                          </span>
                        </div>
                      </div>
                      <div className="relative h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${item.rate >= item.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                          style={{ width: `${item.rate}%` }}
                        />
                        {/* Target marker */}
                        <div
                          className="absolute top-0 bottom-0 w-0.5 bg-gray-800 dark:bg-white"
                          style={{ left: `${item.target}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">0%</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Target: {item.target}%</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">100%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Carrier */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">By Carrier</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Carrier</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Fill Rate</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-300">Shipments</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {fillRateByCarrier.map((carrier) => (
                        <tr key={carrier.carrier}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{carrier.carrier}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{carrier.rate}%</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-500 dark:text-gray-400">{carrier.shipments}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              carrier.rate >= 88 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              carrier.rate >= 85 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {carrier.rate >= 88 ? 'On Target' : carrier.rate >= 85 ? 'Below Target' : 'Needs Attention'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <button
                onClick={() => { setActiveKPIModal(null); navigate('/analytics'); }}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                View full analytics →
              </button>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Transport Modal */}
      {activeKPIModal === 'premium' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                  <BoltIcon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Premium Transport
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    4.2% of shipments required premium/expedited transport
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* What is Premium Transport */}
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
                <p className="text-sm text-orange-800 dark:text-orange-300">
                  <strong>Premium Transport</strong> refers to expedited or unplanned shipments that incur additional costs. Lower is better, as it indicates fewer disruptions and emergencies.
                </p>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{premiumSummary.totalShipments}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Premium Shipments</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{premiumSummary.totalCost}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Cost</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{premiumSummary.avgCostPerShipment}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Avg Cost/Shipment</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">-2.1%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">vs Last Period</p>
                </div>
              </div>

              {/* Reasons Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reasons for Premium Transport</h3>
                <div className="space-y-3">
                  {premiumSummary.mainReasons.map((item) => (
                    <div key={item.reason} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">{item.reason}</span>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900 dark:text-white">{item.count}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({item.percentage}%)</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Premium Shipments */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Premium Shipments</h3>
                <div className="space-y-3">
                  {premiumShipments.map((shipment) => (
                    <div key={shipment.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-teal-600 dark:text-teal-400">{shipment.id}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{shipment.date}</span>
                          </div>
                          <p className="text-sm text-gray-900 dark:text-white mt-1">
                            {shipment.origin} → {shipment.destination}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <span className="text-orange-600 dark:text-orange-400">Reason:</span> {shipment.reason}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Carrier: {shipment.carrier}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-orange-600">{shipment.cost}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <button
                onClick={() => { setActiveKPIModal(null); navigate('/shipments'); }}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                View all shipments →
              </button>
              <button
                onClick={() => setActiveKPIModal(null)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Division Detail Modal */}
      {selectedDivision && divisionDetails[selectedDivision] && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  selectedDivision === 'Chilled' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  selectedDivision === 'Frozen' ? 'bg-cyan-100 dark:bg-cyan-900/30' :
                  selectedDivision === 'Ambient' ? 'bg-amber-100 dark:bg-amber-900/30' :
                  'bg-green-100 dark:bg-green-900/30'
                }`}>
                  <span className="text-3xl">
                    {selectedDivision === 'Chilled' ? '❄️' :
                     selectedDivision === 'Frozen' ? '🧊' :
                     selectedDivision === 'Ambient' ? '📦' : '🥬'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedDivision} Division
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Performance Overview
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      divisionDetails[selectedDivision].trend === 'up' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                      divisionDetails[selectedDivision].trend === 'down' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {getTrendIcon(divisionDetails[selectedDivision].trend)}
                      {divisionDetails[selectedDivision].trendValue}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedDivision(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className={`text-2xl font-bold ${
                    divisionDetails[selectedDivision].dot >= 94 ? 'text-green-600' :
                    divisionDetails[selectedDivision].dot >= 90 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{divisionDetails[selectedDivision].dot}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">DOT</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className={`text-2xl font-bold ${
                    divisionDetails[selectedDivision].cot >= 92 ? 'text-green-600' :
                    divisionDetails[selectedDivision].cot >= 88 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{divisionDetails[selectedDivision].cot}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">COT</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className={`text-2xl font-bold ${
                    divisionDetails[selectedDivision].fillRate >= 85 ? 'text-green-600' :
                    divisionDetails[selectedDivision].fillRate >= 75 ? 'text-yellow-600' : 'text-red-600'
                  }`}>{divisionDetails[selectedDivision].fillRate}%</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fill Rate</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{divisionDetails[selectedDivision].totalShipments.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Shipments</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{divisionDetails[selectedDivision].totalPallets.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pallets</p>
                </div>
              </div>

              {/* Financial & Operational Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4">
                  <p className="text-sm text-teal-600 dark:text-teal-400 font-medium">Revenue</p>
                  <p className="text-xl font-bold text-teal-700 dark:text-teal-300 mt-1">{divisionDetails[selectedDivision].revenue}</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
                  <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">Costs</p>
                  <p className="text-xl font-bold text-orange-700 dark:text-orange-300 mt-1">{divisionDetails[selectedDivision].costs}</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                  <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Avg Lead Time</p>
                  <p className="text-xl font-bold text-purple-700 dark:text-purple-300 mt-1">{divisionDetails[selectedDivision].avgLeadTime}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                  <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Pallets/Shipment</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                    {Math.round(divisionDetails[selectedDivision].totalPallets / divisionDetails[selectedDivision].totalShipments)}
                  </p>
                </div>
              </div>

              {/* Monthly Trend */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Monthly Trend</h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100 dark:bg-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">Month</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">DOT %</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">COT %</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">Fill Rate %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {divisionDetails[selectedDivision].monthlyTrend.map((month) => (
                        <tr key={month.month}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{month.month}</td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`font-medium ${month.dot >= 94 ? 'text-green-600' : month.dot >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {month.dot}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`font-medium ${month.cot >= 92 ? 'text-green-600' : month.cot >= 88 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {month.cot}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <span className={`font-medium ${month.fillRate >= 85 ? 'text-green-600' : month.fillRate >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {month.fillRate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Two Column Layout for Routes and Carriers */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Top Routes */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Routes</h3>
                  <div className="space-y-2">
                    {divisionDetails[selectedDivision].topRoutes.map((route, index) => (
                      <div key={route.route} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-400 text-yellow-900' :
                            index === 1 ? 'bg-gray-300 text-gray-700' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{route.route}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{route.shipments} shipments</p>
                          </div>
                        </div>
                        <span className={`text-sm font-semibold ${route.dot >= 94 ? 'text-green-600' : route.dot >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {route.dot}% DOT
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Carriers */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Carriers</h3>
                  <div className="space-y-2">
                    {divisionDetails[selectedDivision].topCarriers.map((carrier, index) => (
                      <div key={carrier.carrier} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-400 text-yellow-900' :
                            index === 1 ? 'bg-gray-300 text-gray-700' :
                            index === 2 ? 'bg-amber-600 text-white' :
                            'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{carrier.carrier}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{carrier.shipments} shipments</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-yellow-500">★</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{carrier.rating}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Alerts */}
              {divisionDetails[selectedDivision].recentAlerts.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Alerts</h3>
                  <div className="space-y-2">
                    {divisionDetails[selectedDivision].recentAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`rounded-lg p-4 border-l-4 ${
                          alert.severity === 'high' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' :
                          alert.severity === 'medium' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500' :
                          'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                alert.severity === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                                alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                                'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                              }`}>
                                {alert.severity.toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{alert.id}</span>
                            </div>
                            <p className="text-sm text-gray-900 dark:text-white mt-1">{alert.message}</p>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{alert.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex gap-4">
                <button
                  onClick={() => { setSelectedDivision(null); navigate('/shipments'); }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  View shipments →
                </button>
                <button
                  onClick={() => { setSelectedDivision(null); navigate('/analytics'); }}
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                >
                  View analytics →
                </button>
              </div>
              <button
                onClick={() => setSelectedDivision(null)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
